'use client'

import { useEffect } from 'react'
import { reportError } from '@/lib/error-reporting/client'

export function GlobalErrorHandler() {
  useEffect(() => {
    // Handle unhandled errors
    const handleError = (event: ErrorEvent) => {
      event.preventDefault()
      reportError(event.error || event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        type: 'uncaught-error'
      })
    }

    // Handle unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      event.preventDefault()
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason))

      reportError(error, {
        type: 'unhandled-rejection',
        reason: event.reason
      })
    }

    // Handle console errors (optional - captures manual console.error calls)
    const originalConsoleError = console.error
    console.error = (...args: unknown[]) => {
      // Call original console.error
      originalConsoleError.apply(console, args)

      // Check for OAuth-related errors and skip them
      const argsString = args.map(arg => String(arg)).join(' ')
      const isOAuthError = argsString.includes('Failed to fetch RSC payload') &&
                          (argsString.includes('/api/auth/') || argsString.includes('/auth/'))

      // Skip Suspense/hydration errors we're already aware of
      const isSuspenseError = argsString.includes('No group found for id') ||
                              argsString.includes('Hydration') ||
                              argsString.includes('hydration')

      if (isOAuthError || isSuspenseError) {
        // Skip reporting - these are expected or already known
        return
      }

      // Report critical errors (those with Error objects)
      const errorArg = args.find(arg => arg instanceof Error)
      if (errorArg) {
        reportError(errorArg as Error, {
          type: 'console-error',
          allArgs: args.map(arg =>
            arg instanceof Error ? arg.message : String(arg)
          )
        })
      }
    }

    // Add event listeners
    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
      console.error = originalConsoleError
    }
  }, [])

  return null // This component doesn't render anything
}
